import {useForceRemountOnChange} from '@wandb/weave/common/util/hooks';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo} from 'react';

import * as FiltersUtil from '../../../util/filters';
import * as RunUtil from '../../../util/runs';
import {tryJSONParse} from '../../../util/try_json_parse';
import {
  FilterValueSelectorCreatorProps,
  FilterValueSelectorDate,
  FilterValueSelectorTime,
} from '../../Filters/FilterValueSelector';
import {FilterSimpleValueInfiniteDropdown} from '../../InfiniteScrollDropdown/FilterSimpleValueComponents';
import {getCurrentSelections} from '../../InfiniteScrollDropdown/utils';
import {RunsFilterTagNameSelector} from './RunsFilterTagNameSelector';

export type RunFilterValueSelectorCreatorProps =
  FilterValueSelectorCreatorProps<RunUtil.Key>;

type RunFilterValueSelectorProps = RunFilterValueSelectorCreatorProps & {
  entityName: string;
  projectName: string;
};

export const RunsFilterValueSelector = ({
  entityName,
  projectName,
  filter,
  setFilter,
}: RunFilterValueSelectorProps) => {
  const setPartial = (
    partialFilter: Partial<FiltersUtil.IndividualFilter<RunUtil.Key>>
  ) => {
    setFilter({
      ...filter,
      ...partialFilter,
    } as FiltersUtil.IndividualFilter<RunUtil.Key>);
  };

  const filterValueType = FiltersUtil.getFilterValueType(filter);
  switch (filterValueType) {
    case FiltersUtil.ValueType.Duration:
      return (
        <FilterValueSelectorTime
          meta={FiltersUtil.assertIsValueFilter(filter).meta}
          value={filter.value as string}
          setFilter={setPartial}
          minimumUnit={
            filter.op === 'WITHINSECONDS' ? 'minutes' : null
            /* the withinseconds operation isn't guaranteed to be accurate
                 on the scale of seconds and there isn't a known use case. */
          }
        />
      );
    case FiltersUtil.ValueType.Date:
      return (
        <FilterValueSelectorDate
          value={filter.value as string}
          setFilter={setPartial}
        />
      );
    case FiltersUtil.ValueType.Tags: {
      return (
        <RunsFilterTagNameSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter as FiltersUtil.ValueFilter<RunUtil.Key>}
          setFilter={setPartial}
        />
      );
    }
    case FiltersUtil.ValueType.Default:
      return (
        <RunsFilterSimpleValueSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter}
          setFilter={setFilter}
        />
      );
    default:
      assertUnreachable(filterValueType);
  }
};

type RunsFilterSimpleValueSelectorProps = {
  entityName: string;
  projectName: string;
  filter: FiltersUtil.IndividualFilter<RunUtil.Key>;
  setFilter(filter: FiltersUtil.IndividualFilter<RunUtil.Key>): void;
};
const RunsFilterSimpleValueSelector = (
  props: RunsFilterSimpleValueSelectorProps
) => {
  const {filter, setFilter} = props;
  const extraQueryVariables = useMemo(
    () => ({
      keyPath: RunUtil.keyToServerPath(filter.key),
      filters: JSON.stringify(FiltersUtil.toMongo(FiltersUtil.EMPTY_FILTERS)),
    }),
    [filter.key]
  );

  const toggleFilter = useCallback(
    (newSelection: string) => {
      const selectionSet = new Set(getCurrentSelections(filter));

      // Individual filters will replace that item with the new item
      if (!FiltersUtil.isMultiValue(filter)) {
        if (selectionSet.has(newSelection)) {
          setFilter({
            ...filter,
            value: FiltersUtil.EMPTY_FILTER_VALUE,
            disabled: true,
          });
        } else {
          setFilter({
            ...filter,
            value: tryJSONParse(newSelection),
            disabled: false,
          });
        }
      }

      // the multi-value filter will add or subtract based on if it's already included
      else {
        if (selectionSet.has(newSelection)) {
          selectionSet.delete(newSelection);
        } else {
          selectionSet.add(newSelection);
        }

        setFilter({
          ...filter,
          value: [...selectionSet].map(tryJSONParse),
          disabled: false,
        });
      }
    },
    [filter, setFilter]
  );

  // We need to reset the state of the infinite dropdown when the filter key changes.
  // Otherwise, the results from the previous key persist.
  // TODO: Do this, but like, better
  const {shouldRenderNull} = useForceRemountOnChange([filter.key]);

  return (
    <Tailwind style={{display: `contents`}}>
      {!shouldRenderNull && (
        <FilterSimpleValueInfiniteDropdown
          entityName={props.entityName}
          projectName={props.projectName}
          forceExactMatch={['=', '!='].includes(filter.op)}
          extraQueryVariables={extraQueryVariables}
          skip={extraQueryVariables.keyPath === ``}
          isMultiSelect={FiltersUtil.isMultiValue(filter)}
          selections={getCurrentSelections(filter)}
          toggleSelection={toggleFilter}
        />
      )}
    </Tailwind>
  );
};
